import App from 'components/templates/app';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import Section from 'components/Section';
import Subtitle from 'components/Subtitle';
import {
  Box,
  FormControl, GridLegacy as Grid,
  InputLabel,
  List,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
} from '@mui/material';
import Loader from 'components/SectionLoader';
import ShareAcquisitionDialog from 'components/dialogs/ShareAcquisitionDialog';
import AnalyticsDialog from 'components/dialogs/AnalyticsDialog';
import EditPatientDataDialog from 'components/dialogs/EditPatientDataDialog';
import EditMedicalOpinionDialog from 'components/dialogs/EditMedicalOpinionDialog';
import Button from 'components/Button';
import { showErrorToast, showSuccessToast, showWarningToast } from 'features/toastSlice';
import TagsSelectDialog from 'components/dialogs/TagFilterDialog';
import Autocomplete from 'components/Autocomplete';
import ProfileTag from 'components/ProfileChip';
import VisibilitySharpIcon from '@mui/icons-material/VisibilitySharp';
import QueryStatsSharpIcon from '@mui/icons-material/QueryStatsSharp';
import ShareSharpIcon from '@mui/icons-material/ShareSharp';
import ArchiveSharpIcon from '@mui/icons-material/ArchiveSharp';
import UnarchiveSharpIcon from '@mui/icons-material/UnarchiveSharp';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { getDoctorNames } from 'api/portal';
import {
  getAcquisitions,
  getDoctors,
  getPatientHashes,
  getTags,
  reportAuthorization,
  getOrgKey,
  archiveAcquisition,
} from 'api/analytics';
import { decrypt, getReport } from 'api/client-phi';
import { ANALYTICS_REPORTS_URL } from 'config/settings';
import Cookies from 'js-cookie';
import Fade from 'components/Fade';
import AcquisitionRow from 'components/tables/AcquisitionRow';
import { deserializeTabularData } from 'utils/serialization';
import { getEditPatientDataFields } from 'utils/patientsFormConfig';
import {
  clearReportDeepLinkSearchParams,
  normalizeReportDeepLinkAction,
  persistPostLoginRedirect,
  rewriteMemberReportsPathForBraincareAdmin,
} from 'utils/postLoginRedirect';
import AcquisitionMenuItem from 'components/AcquisitionMenuItem';
import styles from './styles.module.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const ACQUISITIONS_FETCH_MAX_ATTEMPTS = 10;
const RETRY_DELAY_MS = 800;

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Rede, timeout/abort do Axios no cliente, e 5xx (incl. 504 gateway timeout). */
function isRetriableAcquisitionsAxiosError(error) {
  if (error?.code === 'ECONNABORTED') {
    return true;
  }
  if (!error?.response) {
    return true;
  }
  const { status } = error.response;
  return status === 500 || status === 502 || status === 503 || status === 504;
}

function parseAcquisitionsResponseBody(response) {
  let responseData;
  if (typeof response.data === 'string') {
    const jsonString = response.data
      .replace(/NaN/g, 'null')
      .replace(/,,/g, ',null,')
      .replace(/,\]/g, ',null]')
      .replace(/,\}/g, ',null}');
    try {
      responseData = JSON.parse(jsonString);
    } catch {
      return { kind: 'parse_error' };
    }
  } else {
    responseData = response.data;
  }
  const { data, error } = responseData;
  if (!error) {
    return { kind: 'success', data };
  }
  return { kind: 'business_error' };
}

function OrganizationReports() {
  const { t } = useTranslation();

  const DEFAULT_DOCTOR = {
    id: -1,
    label: t('physio.physio-personal.dropdown-filter-doctor-all'),
  };

  const [limitPerPage, setLimitPerPage] = useState(10);

  const [anchorEl, setAnchorEl] = useState(null);

  const [protocolToShare, setProtocolToShare] = useState(undefined);
  const [orgToShare, setOrgToShare] = useState(undefined);

  const [decryptData, setDecryptData] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [tags, setTags] = useState([]);

  const [protocol, setProtocol] = useState('');
  const [doctor, setDoctor] = useState(DEFAULT_DOCTOR);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const [acquisitions, setAcquisitions] = useState([]);
  const [acquisitionsFetchSucceeded, setAcquisitionsFetchSucceeded] = useState(null);
  const [patientData, setPatientData] = useState(null);
  const [medicalOpinionData, setMedicalOpinionData] = useState(null);
  const [publicKey, setPublicKey] = useState('');
  const [patientName, setPatientName] = useState('');

  const [selectedAcquisition, setSelectedAcquisition] = useState({});
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const isAcquisitionFinished = selectedAcquisition.status === 'DO';

  const [showAnalyticsDialog, setShowAnalyticsDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showFilterByTags, setShowFilterByTags] = useState(false);
  const [showEditPatientDataDialog, setShowEditPatientDataDialog] = useState(false);
  const [showEditMedicalOpinionDialog, setShowEditMedicalOpinionDialog] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const acquisitionsFetchIdRef = useRef(0);
  const processedOrgReportDeepLinkRef = useRef(null);
  const pendingAutoOpenUmiRef = useRef(null);

  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isBraincareUser = useSelector((state) => state.user.isBraincareUser);

  const activeOrganization = useSelector((state) => state.user.active_organization);
  const activeOrgIdRef = useRef(undefined);
  activeOrgIdRef.current = activeOrganization?.id;
  const analyticsTool = activeOrganization.analytics_tool;

  const protocolId = selectedAcquisition.acquisition_id;

  const protocolEncoded = btoa(protocolId);
  /* eslint-disable-next-line max-len */
  const analyticsURL = `${ANALYTICS_REPORTS_URL}/home#!/analysis-tool/return/Y2xvc2Vfd2luZG93/acquisitions/${protocolEncoded}`;
  const encodedAnalyticsURL = btoa(analyticsURL);
  const encodedLocation = btoa(window.location);
  /* eslint-disable-next-line max-len */
  const authAndAnalyticsRedirectURL = `${ANALYTICS_REPORTS_URL}/auth-gateway?goTo=${encodedAnalyticsURL}&ifFail=${encodedLocation}`;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isBraincareUser) {
      return;
    }
    const current = `${location.pathname}${location.search}${location.hash}`;
    const target = rewriteMemberReportsPathForBraincareAdmin(current);
    if (target !== current) {
      navigate(target, { replace: true });
    }
  }, [isBraincareUser, location.pathname, location.search, location.hash, navigate]);

  const HOME = t('physio.breadcrumbs.home');
  const TITLE = t('physio.breadcrumbs.title-organization');

  const userState = useSelector((state) => state.user);
  const fullName = [userState.profile.first_name, userState.profile.last_name].filter(Boolean).join(' ');

  const [archiveFilter, setArchiveFilter] = useState('unarchived');
  const ARCHIVE_FILTER_OPTIONS = [
    { value: 'unarchived', label: t('physio.physio-personal.filter-archive-unarchived') },
    { value: 'archived', label: t('physio.physio-personal.filter-archive-archived') },
    { value: 'all', label: t('physio.physio-personal.filter-archive-all') },
  ];

  const breadcrumbs = [
    {
      path: '/',
      title: HOME,
    },
    {
      path: '/organization/physio',
      title: TITLE,
      isActive: true,
    },
  ];

  const onFetchDataFailure = () => dispatch(showErrorToast('messages.fetch-data-failure'));

  const handleFetchDoctors = (values) => {
    getDoctorNames(values)
      .then((response) => {
        const { data } = response;
        setLoadingDoctors(false);
        setDoctors([DEFAULT_DOCTOR, ...data.map((doctor) => ({ ...doctor, label: doctor.full_name }))]);
      })
      .catch(() => {
        setLoadingDoctors(false);
        onFetchDataFailure();
      });
  };

  const fetchDoctorHandler = () => {
    const allDoctors =
      acquisitions.length > 0 &&
      acquisitions.every((acq) => (doctors || []).some((doc) => doc.id === Number(acq.doctor_id)));

    if (allDoctors) return;
    setLoadingDoctors(true);

    getDoctors()
      .then((response) => {
        const { data, error } = response.data;

        if (!error && data && !data.error) {
          handleFetchDoctors(data.values);
          return;
        }

        setLoadingDoctors(false);
        onFetchDataFailure();
      })
      .catch(() => {
        setLoadingDoctors(false);
        onFetchDataFailure();
      });
  };

  const fetchDecryptData = async (patientHashes) => {
    if (!patientHashes || Object.keys(patientHashes).length === 0) return;

    const orgId = activeOrganization.physio_org_id;

    try {
      const { keys, values } = patientHashes;
      const hashIndex = patientHashes.keys.indexOf('nameHash');
      const filteredValues = values.filter(
        (valueArray) => !(valueArray[hashIndex] in decryptData || valueArray[hashIndex] === 'Anonymous')
      );

      if (filteredValues.length === 0) return;

      const filteredHashes = {
        keys,
        values: filteredValues,
      };

      const response = await decrypt(orgId, { hashes: filteredHashes });
      const { data, ok } = response.data;

      if (ok) {
        const { keys, values } = data.hashes;

        const decryptedData = Object.fromEntries(
          values.map((valueArray) => [
            valueArray[hashIndex],
            Object.fromEntries(keys.map((key, i) => [key, valueArray[i]])),
          ])
        );

        setDecryptData((prev) => ({ ...prev, ...decryptedData }));
      }
    } catch {
      onFetchDataFailure();
    }
  };

  const decryptPatientData = async (acquisitionData) => {
    try {
      const orgId = acquisitionData.org_id;

      const hashes = {
        keys: [
          'patientHash',
          'name',
          'birthdayHash',
          'birthday',
          'socialNameHash',
          'socialName',
          'medicalRecordHash',
          'medicalRecord',
        ],
        values: [
          [
            acquisitionData.patient_name,
            'name',
            acquisitionData.birth_date,
            'birthday',
            acquisitionData.patient_social_name,
            'socialName',
            acquisitionData.medical_record,
            'medicalRecord',
          ],
        ],
      };

      const response = await decrypt(orgId, { hashes });

      if (response.data.ok) {
        return response.data.data.hashes.values[0];
      }
    } catch (error) {
      console.error('Error decrypting patient data:', error);
      throw error;
    }
    return null;
  };

  const decryptMedicalOpinion = async (acquisitionData) => {
    try {
      const orgId = acquisitionData.org_id;
      const medicalOpinion = acquisitionData.medical_opinion;

      if (!medicalOpinion || !medicalOpinion.text) {
        return {
          acquisition_id: acquisitionData.acquisition_id,
          text: '',
        };
      }

      const hashes = {
        keys: ['medicalOpinionHash', 'medicalOpinion'],
        values: [[medicalOpinion.text, 'medicalOpinion']],
      };

      const response = await decrypt(orgId, { hashes });

      if (response.data.ok) {
        return {
          acquisition_id: acquisitionData.acquisition_id,
          text: response.data.data.hashes.values[0][1],
        };
      }
    } catch (error) {
      console.error('Error decrypting medical opinion data:', error);
      throw error;
    }
    return null;
  };

  const fetchPublicKey = async () => {
    try {
      const response = await getOrgKey();
      if (response.data && response.data.data) {
        setPublicKey(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching public key:', error);
      dispatch(showErrorToast('messages.fetch-public-key-failure'));
    }
  };

  const setLoadingButton = (targetProtocolId, isDownloading) => {
    setAcquisitions((prev) =>
      prev.map((acquisition) => {
        const protocolId = acquisition.acquisition_id;
        return {
          ...acquisition,
          isDownloading: protocolId === targetProtocolId ? isDownloading : acquisition.isDownloading,
        };
      }),
    );
  };

  const setAcquisitionAsDownloading = (targetProtocolId, isDownloading) => {
    setAcquisitions((prev) =>
      prev.map((acquisition) => {
        const protocolId = acquisition.acquisition_id;
        return {
          ...acquisition,
          isDownloading: protocolId === targetProtocolId ? isDownloading : acquisition.isDownloading,
        };
      }),
    );
  };



  const handleEditPatientData = async (acquisitionId) => {
    try {
      setLoadingButton(protocolId, true);
      const response = await getAcquisitions({
        filters: {
          patient_names: [],
          acquisition_id: [acquisitionId],
          doctor_id: '',
        },
        paging: {
          page: 1,
          limit: 1,
        },
      });

      if (response.data && response.data.data && response.data.data.acquisitions.values.length > 0) {
        const acquisitionData = deserializeTabularData(
          response.data.data.acquisitions.keys,
          response.data.data.acquisitions.values[0],
        );

        if (acquisitionData.status !== 'DO') {
          dispatch(showErrorToast(t('physio.physio-edit-patient-dialog.monitorization-not-finished')));
          setLoadingButton(protocolId, false);
          return;
        }

        const decryptedData = await decryptPatientData(acquisitionData);

        const patientData = {
          acquisition_id: acquisitionData.acquisition_id,
          patient_name: decryptedData[1],
          patient_social_name: decryptedData[5],
          birth_date: decryptedData[3],
          age: acquisitionData.age,
          start_time: acquisitionData.start_time,
          gender: acquisitionData.gender,
          medical_record: decryptedData[7],
          status: acquisitionData.status,
        };

        fetchPublicKey();
        setPatientData(patientData);
        setShowEditPatientDataDialog(true);
      } else {
        dispatch(showErrorToast('messages.acquisition-not-found'));
      }
    } catch (error) {
      console.error('Error fetching acquisition:', error);
      dispatch(showErrorToast('messages.fetch-data-failure'));
    } finally {
      setLoadingButton(protocolId, false);
    }
  };

  const handleEditMedicalOpinion = async (acquisitionId) => {
    try {
      setLoadingButton(protocolId, true);
      const response = await getAcquisitions({
        filters: {
          patient_names: [],
          acquisition_id: [acquisitionId],
          doctor_id: '',
        },
        paging: {
          page: 1,
          limit: 1,
        },
      });

      if (response.data && response.data.data && response.data.data.acquisitions.values.length > 0) {
        const acquisitionData = deserializeTabularData(
          response.data.data.acquisitions.keys,
          response.data.data.acquisitions.values[0],
        );

        const decryptedData = await decryptMedicalOpinion(acquisitionData);
        const patientData = await decryptPatientData(acquisitionData);

        fetchPublicKey();
        setMedicalOpinionData(decryptedData);
        setPatientName(patientData[1]);
        setShowEditMedicalOpinionDialog(true);
      } else {
        dispatch(showErrorToast('messages.acquisition-not-found'));
      }
    } catch (error) {
      console.error('Error fetching acquisition:', error);
      dispatch(showErrorToast('messages.fetch-data-failure'));
    } finally {
      setLoadingButton(protocolId, false);
    }
  };

  const isPatientNameDecrypted = (patientName, decryptedByHash) =>
    patientName === 'Anonymous' || patientName in (decryptedByHash || {});

  const fetchPatientsHashes = async (acquisitionsToDecrypt = acquisitions) => {
    setLoadingPatients(true);

    try {
      const allDecrypted =
        acquisitionsToDecrypt.length > 0 &&
        acquisitionsToDecrypt.every((acq) => isPatientNameDecrypted(acq.patient_name, decryptData));

      if (!allDecrypted) {
        const response = await getPatientHashes();
        const { data, error } = response.data;

        if (!error) {
          await fetchDecryptData(data);
        }
      }
    } catch {
      onFetchDataFailure();
    } finally {
      setLoadingPatients(false);
    }
  };

  const fetchTags = () => {
    const allTags =
      acquisitions.length > 0 &&
      acquisitions.every((acq) => acq.tags.every((t) => t === '' || tags.includes(t)));

    if (allTags) return;


    getTags()
      .then((response) => {
        const { data, error } = response.data;

        if (error) {
          onFetchDataFailure();
          return;
        }

        setTags(data.values);
      })
      .catch(() => {
        onFetchDataFailure();
      });
  };

  const fetchAcquisitions = ({ 
    selectedPatients,
    selectedTags,
    protocol,
    doctor,
    currentPage,
    limitPerPage,
    archiveFilter,
    enableInitialLoadRetry = false,
  }) => {
    const doctorId = doctor && doctor.id !== -1 ? doctor.id : '';

    const filters = {
      patient_names: selectedPatients.map((patient) => patient.nameHash),
      tags: selectedTags,
      acquisition_id: [protocol],
      doctor_id: String(doctorId),
    };

    if (archiveFilter === 'archived') {
      filters.is_archived = true;
    } else if (archiveFilter === 'unarchived') {
      filters.is_archived = false;
    }

    setLoading(true);
    setCurrentPage(currentPage);
    setAcquisitionsFetchSucceeded(null);

    const payload = {
      filters,
      paging: {
        page: currentPage,
        limit: limitPerPage,
      },
    };

    if (!enableInitialLoadRetry) {
      getAcquisitions(payload)
        .then((response) => {
          let responseData;

          if (typeof response.data === 'string') {
            const jsonString = response.data
              .replace(/NaN/g, 'null')
              .replace(/,,/g, ',null,')
              .replace(/,\]/g, ',null]')
              .replace(/,\}/g, ',null}');

            try {
              responseData = JSON.parse(jsonString);
            } catch {
              setLoading(false);
              setAcquisitionsFetchSucceeded(false);
              onFetchDataFailure();
              return;
            }
          } else {
            responseData = response.data;
          }

          const { data, error } = responseData;

          setLoading(false);

          if (!error) {
            const acquisitionKeys = data.acquisitions.keys;
            const valueRows = data.acquisitions.values ?? [];

            const nextAcquisitions = valueRows.map((acquisition) =>
              deserializeTabularData(acquisitionKeys, acquisition),
            );

            setAcquisitions(nextAcquisitions);
            setCount(Math.ceil((data.acquisitions.paging?.count ?? 0) / limitPerPage));
            setAcquisitionsFetchSucceeded(true);

            fetchPatientsHashes(nextAcquisitions);
            fetchDoctorHandler();
            fetchTags();
            return;
          }

          setAcquisitionsFetchSucceeded(false);
          onFetchDataFailure();
        })
        .catch(() => {
          setLoading(false);
          setAcquisitionsFetchSucceeded(false);
          onFetchDataFailure();
        });
      return;
    }

    acquisitionsFetchIdRef.current += 1;
    const fetchId = acquisitionsFetchIdRef.current;

    const fail = () => {
      if (fetchId !== acquisitionsFetchIdRef.current) {
        return;
      }
      setLoading(false);
      setAcquisitionsFetchSucceeded(false);
      onFetchDataFailure();
    };

    const runAttempt = async (attempt) => {
      if (fetchId !== acquisitionsFetchIdRef.current) {
        return;
      }

      try {
        const response = await getAcquisitions(payload);
        if (fetchId !== acquisitionsFetchIdRef.current) {
          return;
        }
        const parsed = parseAcquisitionsResponseBody(response);

        if (parsed.kind === 'success') {
          if (fetchId !== acquisitionsFetchIdRef.current) {
            return;
          }

          const { data } = parsed;

          setLoading(false);

          const acquisitionKeys = data.acquisitions.keys;
          const valueRows = data.acquisitions.values ?? [];

          const nextAcquisitions = valueRows.map((acquisition) =>
            deserializeTabularData(acquisitionKeys, acquisition),
          );

          setAcquisitions(nextAcquisitions);
          setCount(Math.ceil((data.acquisitions.paging?.count ?? 0) / limitPerPage));
          setAcquisitionsFetchSucceeded(true);

          fetchPatientsHashes(nextAcquisitions);
          fetchDoctorHandler();
          fetchTags();
          return;
        }

        if (parsed.kind === 'parse_error' || parsed.kind === 'business_error') {
          fail();
        }
      } catch (error) {
        if (fetchId !== acquisitionsFetchIdRef.current) {
          return;
        }

        if (isRetriableAcquisitionsAxiosError(error) && attempt < ACQUISITIONS_FETCH_MAX_ATTEMPTS - 1) {
          await sleep(RETRY_DELAY_MS);
          if (fetchId !== acquisitionsFetchIdRef.current) {
            return;
          }
          await runAttempt(attempt + 1);
          return;
        }

        fail();
      }
    };

    (async () => {
      await runAttempt(0);
    })();
  };

  const activeOrganizationId = activeOrganization?.id;

  const handleArchiveAcquisition = async (acquisitionId, shouldArchive) => {
    try {
      setAcquisitionAsDownloading(acquisitionId, true);

      const response = await archiveAcquisition(acquisitionId, shouldArchive);

      if (response.status === 204) {
        setAcquisitions(
          acquisitions.map((acq) =>
            acq.acquisition_id === acquisitionId ? { ...acq, is_archived: shouldArchive } : acq,
          ),
        );

        dispatch(
          showSuccessToast(
            shouldArchive ? t('physio.physio-personal.archive-success') : t('physio.physio-personal.unarchive-success'),
          ),
        );

        fetchAcquisitions({
          selectedPatients,
          selectedTags,
          protocol,
          doctor,
          currentPage,
          limitPerPage,
          archiveFilter,
        });
      }
    } catch (error) {
      console.error('Archive error:', error);
      dispatch(showErrorToast(t('physio.physio-personal.archive-error')));
    } finally {
      setAcquisitionAsDownloading(acquisitionId, false);
      setAnchorEl(null);
    }
  };

  const filterAcquisitions = () => {
    fetchAcquisitions({
      selectedPatients,
      selectedTags,
      protocol,
      doctor,
      currentPage: 1,
      limitPerPage,
      archiveFilter,
    });
  };

  const clearFilters = () => {
    setSelectedPatients([]);
    setInputValue('');
    setProtocol('');
    setDoctor(DEFAULT_DOCTOR);
    setSelectedTags([]);
    setArchiveFilter('all');

    fetchAcquisitions({
      selectedPatients: [],
      selectedTags: [],
      protocol: '',
      doctor: DEFAULT_DOCTOR,
      currentPage: 1,
      limitPerPage: 10,
      archiveFilter: 'all',
    });
  };

  const onPaginationChange = (event, value) => {
    fetchAcquisitions({
      selectedPatients,
      selectedTags,
      protocol,
      doctor,
      currentPage: value,
      limitPerPage,
    });
  };

  const onItensPerPageChange = (event, value) => {
    const newLimit = value;
    setLimitPerPage(newLimit);

    fetchAcquisitions({
      selectedPatients,
      selectedTags,
      protocol,
      doctor,
      currentPage: 1,
      limitPerPage: newLimit,
      archiveFilter,
    });
  };

  const openReportWhenAuthorized = (protocolId, url) => {
    let timeOutCount = 0;

    const interval = setInterval(() => {
      fetch(url).then((res) => {
        timeOutCount += 1;
        // open the report in a new tab
        if (res.status === 200) {
          setAcquisitionAsDownloading(protocolId, false);
          window.open(url, '_blank');
          clearInterval(interval);
        }
        // timeout after 3 minutes (180 attempts)
        if (timeOutCount >= 180) {
          // eslint-disable-next-line no-alert
          alert('Report error, please try again');
          setAcquisitionAsDownloading(protocolId, false);
          clearInterval(interval);
        }
      });
    }, 1000);
  };

  const downloadReport = (acquisition) => {
    const protocolId = acquisition.acquisition_id;
    const orgId = acquisition.org_id;

    setAcquisitionAsDownloading(protocolId, true);

    reportAuthorization({
      acquisition_id: protocolId,
      report_language: i18next.language,
      report_format: 'PDF',
    })
      .then((response) => {
        const { data, error } = response.data;

        if (error) {
          setAcquisitionAsDownloading(protocolId, false);
          onFetchDataFailure();
          return;
        }

        getReport(orgId, data).then((response) => {
          const { data } = response;
          openReportWhenAuthorized(protocolId, data.data);
        });
      })
      .catch(() => {
        setAcquisitionAsDownloading(protocolId, false);
        onFetchDataFailure();
      });
  };

  useEffect(() => {
    if (!activeOrganization?.id || activeOrganizationId === -1) {
      return undefined;
    }

    const qs = new URLSearchParams(location.search);
    const umi = qs.get('umi');
    const orgIdParamRaw = qs.get('org_id');
    if (!umi?.trim() || orgIdParamRaw == null || String(orgIdParamRaw).trim() === '') {
      return undefined;
    }

    if (isBraincareUser) {
      const path = `${location.pathname}${location.search}${location.hash}`;
      const target = rewriteMemberReportsPathForBraincareAdmin(path);
      if (target !== path) {
        navigate(target, { replace: true });
      }
      return undefined;
    }

    const orgIdParamNorm = String(orgIdParamRaw).trim();
    const actionRaw = qs.get('action') || '';
    const linkKey = `${umi.trim()}:${orgIdParamNorm}:${actionRaw}`;
    if (processedOrgReportDeepLinkRef.current === linkKey) {
      return undefined;
    }

    let cancelled = false;

    const runDeepLink = () => {
      if (cancelled) {
        return;
      }

      const liveOrgId = activeOrgIdRef.current;
      if (!liveOrgId || liveOrgId === -1) {
        return;
      }

      if (orgIdParamNorm !== String(liveOrgId).trim()) {
        processedOrgReportDeepLinkRef.current = linkKey;
        const deepLinkPath = `${location.pathname}${location.search}${location.hash}`;
        const stored = persistPostLoginRedirect(deepLinkPath);
        if (stored) {
          navigate('/auth/direct-login', { replace: true });
          return;
        }
        dispatch(showWarningToast(t('auth.login.email-link-wrong-org')));
        clearReportDeepLinkSearchParams(setSearchParams);
        return;
      }

      const action = normalizeReportDeepLinkAction(actionRaw);
      const umiTrim = umi.trim();

      processedOrgReportDeepLinkRef.current = linkKey;
      setProtocol(umiTrim);
      setArchiveFilter('all');
      pendingAutoOpenUmiRef.current = action === 'open' ? umiTrim : null;

      fetchAcquisitions({
        selectedPatients: [],
        selectedTags: [],
        protocol: umiTrim,
        doctor: DEFAULT_DOCTOR,
        currentPage: 1,
        limitPerPage,
        archiveFilter: 'all',
        enableInitialLoadRetry: true,
      });

      clearReportDeepLinkSearchParams(setSearchParams);
    };

    const timeoutId = window.setTimeout(runDeepLink, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    activeOrganizationId,
    location.pathname,
    location.search,
    location.hash,
    dispatch,
    isBraincareUser,
    limitPerPage,
    navigate,
    setSearchParams,
    t,
  ]);

  useEffect(
    () => () => {
      acquisitionsFetchIdRef.current += 1;
    },
    [],
  );

  useEffect(() => {
    if (!activeOrganization || activeOrganizationId === -1) {
      return undefined;
    }

    const qsLoad = new URLSearchParams(location.search);
    const hasReportDeepLink =
      Boolean(qsLoad.get('umi')?.trim()) && Boolean(qsLoad.get('org_id')?.trim());
    if (hasReportDeepLink) {
      return undefined;
    }

    fetchAcquisitions({
      selectedPatients,
      selectedTags,
      protocol,
      doctor,
      currentPage,
      limitPerPage,
      archiveFilter,
      enableInitialLoadRetry: true,
    });

    return undefined;
  }, [activeOrganizationId, location.search]);

  useEffect(() => {
    const pendingUmi = pendingAutoOpenUmiRef.current;
    if (!pendingUmi) {
      return undefined;
    }

    if (loading) {
      return undefined;
    }

    if (acquisitionsFetchSucceeded === false) {
      pendingAutoOpenUmiRef.current = null;
      return undefined;
    }

    if (acquisitionsFetchSucceeded !== true) {
      return undefined;
    }

    pendingAutoOpenUmiRef.current = null;

    const acq = acquisitions.find((a) => String(a.acquisition_id) === String(pendingUmi));
    if (acq) {
      downloadReport(acq);
    }

    return undefined;
  }, [acquisitions, acquisitionsFetchSucceeded, loading]);

  const openShareReportDialog = (protocolId, orgId) => {
    setProtocolToShare(protocolId);
    setOrgToShare(orgId);
    setShowShareDialog(true);
  };

  const closeShareDialog = (shared) => {
    setShowShareDialog(false);
    if (shared) {
      fetchAcquisitions({
        selectedPatients,
        selectedTags,
        protocol,
        doctor,
        currentPage,
      });
    }
  };

  const renderActionPopover = () => {
    const protocolId = selectedAcquisition.acquisition_id;
    const orgId = selectedAcquisition.org_id;
    const isShared = selectedAcquisition.shared;

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const isArchived = selectedAcquisition?.is_archived;

    return (
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <List>
          <AcquisitionMenuItem
            icon={<VisibilitySharpIcon />}
            onClick={() => {
              downloadReport(selectedAcquisition);
              setAnchorEl(null);
            }}
            data-testid="view-report-item"
            text={t('physio.physio-personal.button-report-view')}
          />
          {analyticsTool && (
            <AcquisitionMenuItem
              icon={<QueryStatsSharpIcon />}
              onClick={() => {
                if (activeOrganization.country_new.message_analytics) {
                  setShowAnalyticsDialog(true);
                } else {
                  const form = document.createElement('form');
                  form.action = authAndAnalyticsRedirectURL;
                  form.method = 'POST';
                  form.target = '_blank';

                  const input = document.createElement('input');
                  input.type = 'hidden';
                  input.name = 'token';
                  input.value = Cookies.get('access_token');
                  form.appendChild(input);

                  document.body.appendChild(form);
                  form.submit();
                  document.body.removeChild(form);
                }
                setAnchorEl(null);
              }}
              data-testid="analytics-item"
              text={t('physio.physio-personal.button-analytics-tool')}
            />
          )}

          {!isShared && (
            <AcquisitionMenuItem
              icon={<ShareSharpIcon />}
              onClick={() => {
                openShareReportDialog(protocolId, orgId);
                setAnchorEl(null);
              }}
              data-testid="share-report-item"
              text={t('physio.physio-personal.button-report-share')}
            />
          )}

          <AcquisitionMenuItem
            icon={<EditSharpIcon />}
            onClick={() => {
              handleEditPatientData(selectedAcquisition.acquisition_id);
              setAnchorEl(null);
            }}
            disabled={!isAcquisitionFinished}
            data-testid="edit-patient-data-item"
            text={t('physio.physio-personal.button-edit-patient-data')}
          />

          <AcquisitionMenuItem
            icon={<EditSharpIcon />}
            onClick={() => {
              handleEditMedicalOpinion(selectedAcquisition.acquisition_id);
              setAnchorEl(null);
            }}
            data-testid="edit-medical-opinion-item"
            text={t('physio.physio-personal.button-edit-medical-opinion')}
          />

          <AcquisitionMenuItem
            icon={isArchived ? <UnarchiveSharpIcon /> : <ArchiveSharpIcon />}
            onClick={() => {
              handleArchiveAcquisition(selectedAcquisition.acquisition_id, !isArchived);
            }}
            disabled={!isAcquisitionFinished}
            data-testid="archive-item"
            text={
              isArchived ? t('physio.physio-personal.button-unarchive') : t('physio.physio-personal.button-archive')
            }
          />
        </List>
      </Popover>
    );
  };

  const renderMonitorizationTable = () =>
    acquisitions && acquisitions.length > 0 ? (
      <Box>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left">{t('physio.physio-personal.table-physio-protocol')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-patient')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-date')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-p2p1')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-ttp')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-duration')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-doctor')}</TableCell>
                <TableCell align="left">{t('physio.physio-personal.table-physio-status')}</TableCell>
                <TableCell align="left">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {acquisitions.map((acquisition) => (
                <AcquisitionRow
                  key={acquisition.acquisition_id}
                  loadingDoctor={loadingDoctors}
                  loadingPatient={loadingPatients}
                  acquisition={acquisition}
                  decryptData={decryptData}
                  doctors={doctors}
                  onClickMenu={(event, acquisition) => {
                    setSelectedAcquisition(acquisition);
                    setAnchorEl(event.currentTarget);
                  }}
                  onClickProtocol={(acquisition) => downloadReport(acquisition)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {renderActionPopover()}
      </Box>
    ) : (
      <Typography
        variant="body2"
        color="secondary"
        data-testid={
          acquisitionsFetchSucceeded === true ? 'no-monitorization-text' : 'acquisitions-fetch-error-text'
        }
      >
        {acquisitionsFetchSucceeded === true
          ? t('physio.physio-personal.no-monitorizations-found')
          : t('messages.fetch-data-failure')}
      </Typography>
    );

  const patientOptions = Object.values(decryptData).map((item, index) => ({
    id: index,
    nameHash: item.nameHash,
    label: item.name,
  }));

  return (
    <App title={TITLE} breadcrumbs={breadcrumbs}>
      <Section className={styles.sectionFilters}>
        <Subtitle>{t('physio.physio-personal.title-filter-physio')}</Subtitle>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <Grid marginTop="8px" container spacing="12px">
            <Grid item xs={12}>
              <Autocomplete
                multiple
                value={selectedPatients}
                inputValue={inputValue}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                onChange={(event, newValue) => setSelectedPatients([...newValue])}
                options={patientOptions}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <ProfileTag key={index} name={option.label} {...getTagProps({ index })} />
                  ))
                }
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option.id} data-testid={`doctor-option-${option.id}`}>
                    {option.label}
                  </MenuItem>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    data-testid="doctor-text-field"
                    label={t('physio.physio-personal.input-filter-name')}
                  />
                )}
                data-testid="patient-select"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel>{t('physio.physio-personal.input-filter-protocol')}</InputLabel>
                <OutlinedInput
                  name="protocol"
                  value={protocol}
                  onChange={(event) => setProtocol(event.target.value)}
                  inputProps={{ 'data-testid': 'protocol-input' }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <Autocomplete
                  disablePortal
                  value={doctor}
                  onChange={(event, newValue) => setDoctor(newValue)}
                  options={doctors}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderOption={(props, option) => (
                    <Typography
                      {...props}
                      variant="body2"
                      color="secondary"
                      key={option.id}
                      data-testid={`doctor-option-${option.id}`}
                    >
                      {option.label}
                    </Typography>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t('physio.physio-personal.input-filter-doctor')} />
                  )}
                  data-testid="doctor-select"
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <FormControl sx={{ margin: 0 }} fullWidth>
                <InputLabel>{t('physio.physio-personal.input-filter-archive')}</InputLabel>
                <Select
                  value={archiveFilter}
                  onChange={(event) => setArchiveFilter(event.target.value)}
                  label={t('physio.physio-personal.input-filter-archive')}
                  data-testid="archive-filter-select"
                >
                  {ARCHIVE_FILTER_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <Button variant="outlined" onClick={() => setShowFilterByTags(true)} fullWidth data-testid="tag-button">
                {selectedTags.length === 0
                  ? t('physio.physio-personal.button-filter-by-tag')
                  : t('physio.physio-personal.button-filter-by-tag-selected', { number_tags: selectedTags.length })}
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <Button variant="outlined" onClick={clearFilters} fullWidth data-testid="reset-button">
                {t('physio.physio-personal.button-filter-reset')}
              </Button>{' '}
            </Grid>
            <Grid item xs={12} sm={4} md={2.4}>
              <Button type="submit" color="gradient" onClick={filterAcquisitions} fullWidth data-testid="apply-button">
                {t('physio.physio-personal.button-filter-apply')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Section>

      <Section>
        <Loader loading={loading}>
          <Fade>{renderMonitorizationTable()}</Fade>
        </Loader>
      </Section>

      <AnalyticsDialog
        open={showAnalyticsDialog}
        onClose={() => setShowAnalyticsDialog(false)}
        selectedAcquisition={selectedAcquisition}
      />

      <ShareAcquisitionDialog
        open={showShareDialog}
        protocolToShare={protocolToShare}
        orgToShare={orgToShare}
        onClose={closeShareDialog}
      />

      <EditPatientDataDialog
        open={showEditPatientDataDialog}
        onClose={(saved) => {
          setShowEditPatientDataDialog(false);
          if (saved) {
            fetchAcquisitions({
              selectedPatients,
              selectedTags,
              protocol,
              doctor,
              currentPage,
              limitPerPage,
              archiveFilter,
            });
          }
        }}
        patientData={patientData}
        publicKey={publicKey}
        fields={getEditPatientDataFields(activeOrganization)}
      />

      <EditMedicalOpinionDialog
        open={showEditMedicalOpinionDialog}
        onClose={(saved) => {
          setShowEditMedicalOpinionDialog(false);
          if (saved) {
            fetchAcquisitions({
              selectedPatients,
              selectedTags,
              protocol,
              doctor,
              currentPage,
              limitPerPage,
              archiveFilter,
            });
          }
        }}
        medicalOpinion={medicalOpinionData}
        publicKey={publicKey}
        patientName={patientName}
        editorName={fullName}
      />

      <TagsSelectDialog
        tags={tags}
        selectedTags={selectedTags}
        show={showFilterByTags}
        onCheckboxClick={(tag) => {
          if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter((selectedTag) => selectedTag !== tag));
            return;
          }

          setSelectedTags([...selectedTags, tag]);
        }}
        onDialogToggle={() => setShowFilterByTags(false)}
      />

      <Box display="flex" justifyContent="flex-end" alignItems="baseline" mt={2}>
        <Pagination count={count} onChange={onPaginationChange} page={currentPage} />
        <FormControl sx={{ width: 115, marginLeft: 3 }}>
          <InputLabel>{t('physio.physio-personal.input-items-per-page')}</InputLabel>
          <Select
            value={limitPerPage}
            onChange={(event) => onItensPerPageChange(event, event.target.value)}
            label={t('physio.physio-personal.input-items-per-page')}
          >
            {LIMIT_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </App>
  );
}

export default OrganizationReports;
